import { PspRules } from "@CommonTypes/backend/PspRules";

/**
 * The configuration copilot: a conversation that sets the planogram rules
 * (`PspRules`) instead of seventy controls.
 *
 * It never adds a setting. Every change it proposes is a patch over paths that
 * already exist in `PspRules`, the user applies it, and the run is generated
 * through the ordinary routes with the ordinary payload.
 *
 * The conversation is stateless on the server: the page sends the rules it is
 * holding and the recent turns with every request.
 */
export const RoutePostConfigCopilotBrief = "/merchflows/:merchflowId/config-copilot/brief";
/** The opening's words, asked for after the brief renders. */
export const RoutePostConfigCopilotOpening = "/merchflows/:merchflowId/config-copilot/opening";
export const RoutePostConfigCopilotTurn = "/merchflows/:merchflowId/config-copilot/turn";
export const RoutePostConfigCopilotReadout = "/merchflows/:merchflowId/config-copilot/readout";
/** O's one line on a store run, for the Store Planograms page. Read off the store rows only. */
export const RouteGetConfigCopilotVerdict = "/merchflows/:merchflowId/config-copilot/verdict";

/**
 * How much weight a statement can bear.
 * - `measured`: seen on generated planograms.
 * - `from_code`: follows from what the solver does, not yet measured.
 * - `untested`: a lead, no more.
 */
export type CopilotEvidence = "measured" | "from_code" | "untested";

/**
 * How the base planogram is blocked, as the studio reads it
 * (`measureBlockingOf`). Measured in the browser, which holds the planogram.
 */
export interface CopilotBlockingReading {
  role: "vertical" | "horizontal" | "ambiguous";
  /** The attribute or level the reading is about, e.g. "Brand". */
  attribute: string;
  /** 0..1. At or above 0.5 reads vertical, at or below 0.3 horizontal. */
  score: number;
}

export type CopilotFitVerdict = "fits" | "tight" | "cannot_hold_range" | "cannot_fill";

/** One store's wall against what its range needs. Shares are of the wall's width. */
export interface CopilotStoreFit {
  store: string;
  bays: number;
  shelves: number;
  products: number;
  /** Every product at one facing. Above 1 the wall cannot hold the range. */
  needAtOneFacing: number;
  /** Every product at the facings its minimum days of supply needs. */
  needAtMinSupply: number;
  /** Every product at the facings its maximum days of supply allows. Below 1 the wall cannot be filled. */
  fillAtMaxSupply: number;
  verdict: CopilotFitVerdict;
  /**
   * Where the range cannot fill the wall: the smallest share over maximum units
   * (`units_too_many`, in steps of 0.05, at most 1) that fills it, and the fill
   * at that share. A fill under 1 means even the most allowed cannot fill it.
   */
  unitsToFill?: {
    share: number;
    fill: number;
    /**
     * The same share and fill with the data feed's `max_facings` given way (a
     * finite `facings_too_many`), the units cap still held. The feed's cap is
     * what holds the wall where `fill` is under 1 and this fill is higher.
     */
    feedCapLifted?: { share: number; fill: number };
  };
  /**
   * Brands too small to fill a column one product wide down the bay: each still
   * takes a column of its own under brand blocks. A wall with many of them pays
   * for brand blocks in products however much room the range leaves.
   */
  slivers?: number;
  /** Brand blocks cost this wall products: too many sliver brands. */
  blockTight?: boolean;
  /**
   * What the run these walls were measured on (`fitsRun`) did on this wall,
   * where its response was read: its low and bare shelves, what its settings
   * moved there, and the brand block its wall plan made wider than its
   * products fill (`CopilotStoreResult`).
   */
  ran?: Pick<
    CopilotStoreResult,
    | "lowShelves"
    | "bareShelves"
    | "fill"
    | "wall"
    | "pastSupply"
    | "pastFeedCap"
    | "blockPlanEmpty"
    | "overWideBlock"
  >;
}

/**
 * The newest run in one blocking mode. Only runs like the newest run are
 * compared: where the newest run carries no scores, scored runs before it came
 * from another builder and are left out.
 */
export interface CopilotMeasuredMode {
  mode: "vertical" | "horizontal";
  storePogTriggerId: number;
  /** When the run was made. */
  createdAt?: string;
  /** One score per scored store, lowest first. Empty when the run's stores carry no score. */
  scores: number[];
  /** Stores at or above 80. */
  good: number;
  /** Stores in the run, and those that generated. */
  stores?: number;
  generated?: number;
  /** Products left out over the stores that generated. */
  missing?: number;
  /**
   * A run with stores that never finished: how many never came back, and
   * whether others are still going. Absent when every store finished.
   */
  unfinished?: number;
  going?: boolean;
  /** Shelves under three quarters full (bare ones not counted) over the stores that generated, where their responses were read. */
  lowShelves?: number;
  /** Shelves with nothing on them over the same stores, where their responses were read. */
  bareShelves?: number;
  /** Stores whose solve stopped at the time limit, where their responses were read. */
  outOfTime?: number;
  /**
   * Where the two modes' newest runs generated different stores: this run's
   * figures over the stores both generated (shelves and time limits only where
   * every one of them was read in both). The modes are compared on these, never
   * on totals over different stores.
   */
  bothRan?: {
    stores: number;
    missing: number;
    lowShelves?: number;
    bareShelves?: number;
    outOfTime?: number;
  };
  /** When the scores (and their brand-block split) come from an earlier scored run than this one. */
  scoredAt?: string;
  /**
   * The same scores split by whether the store kept brands in blocks of their
   * own (vertical runs on the wall planner only). Absent when it was not read.
   */
  brandBlocks?: { on: number[]; off: number[] };
}

/** Inches or centimetres: what a category's lengths are in. */
export type CopilotLengthUnit = "in" | "cm";

/** Products placed on a run's walls, counted by how high they stacked. */
export interface CopilotStackHeights {
  stores: number;
  /** Products placed, over the stores read; a product placed twice counts twice. */
  placed: number;
  /** At their max high, max high over 1, with room under the shelf above for another unit high. */
  stoppedByMaxHigh: number;
  /** Under their max high with room for another unit high: what filling high alone would raise. */
  shortOfShelfAbove: number;
  /** Placed with a max high of 1: products that don't stack. */
  maxHighOne: number;
}

export interface CopilotFactSheet {
  merchflowId: number;
  basePogId: number;
  blocking: CopilotBlockingReading | null;
  /** The base planogram's aesthetics score, 0..100. */
  baseAesthetics: number | null;
  storeCount: number;
  copyCompatibility?: {
    eligible: number;
    total: number;
    matchingFixtures?: number;
    rangeDiffers?: number;
    reasons: { reason: string; stores: number }[];
    overrideCosts?: { reason: string; stores: number }[];
  };
  /** How many stores have each bay count. */
  bayCounts: { bays: number; stores: number }[];
  /** The stores measured against their range: the smallest, the largest and the tightest. */
  fits: CopilotStoreFit[];
  products: {
    count: number;
    withoutSales: number;
    withoutSupplyDays: number;
    /** Share of products at or above the tall-item line. */
    tall: number;
    /** The tallest product's height, in the requests' own lengths (hundredths). */
    tallest?: number;
    /** Every product's height, lowest first, in the requests' own lengths: a share over any line. */
    heights?: number[];
    /**
     * The smallest shortfall on minimum days of supply that lets most products
     * stay on one unit short: the allowed shortfall is rounded down to whole
     * units, so a share under one over a product's minimum units allows none.
     * Capped at 1.
     */
    supplyShortfallForOneUnit?: number;
    /**
     * The clear height above the lowest and the highest shelf of the walls read,
     * in the requests' own lengths: what says whether any pack is too tall for
     * a shelf it could stand on.
     */
    shelfHeights?: { lowest: number; highest: number };
  };
  /** Products the base planogram flags core range. Absent when the base has no layout. */
  baseCoreRange?: number;
  /**
   * The base planogram's tall products (at or above the tall line), and how
   * many of them stand on the bottom two shelves of a bay with three or more.
   * Absent when the base has no layout or no length unit is known.
   */
  baseTall?: { tall: number; low: number };
  /**
   * What the last run's store requests carried, over those read: how many laid
   * the wall out from the base planogram's own plan, and how many flagged a
   * core range.
   */
  requestsRead?: { stores: number; baseMudmap: number; coreRange: number };
  /**
   * How high the last run's stores stacked their products, over the stores
   * read: each product placed, against its max high and the room under the
   * shelf above. Absent where no store's request and plan could be read.
   */
  stackHeights?: CopilotStackHeights;
  /**
   * The unit the stores' lengths are in (hundredths of it), as read off their
   * walls. O says sizes in it and turns a size the user gives into it. Absent
   * when no wall was read or its height fits neither unit.
   */
  lengthUnit?: CopilotLengthUnit;
  /** Where the store measurements came from. */
  source: "store_requests" | "fixtures_only";
  /**
   * Whether CDT Studio has released a plan for this base planogram on this
   * merchflow (a `merchflow_cdt_release` row). With one, O builds the stores on
   * it (`cdt_source` CATEGORY_TREE); the data feed's own labels would skip it.
   */
  hasRelease?: boolean;
  /**
   * Whether the last run laid its stores out from the studio's rules. When it
   * did, generation raises the fill prices and makes the facings ceiling hard.
   */
  storeRules: "applied" | "not_applied" | "unknown";
  /**
   * The newest generated run of this base planogram in each blocking mode, with
   * its stores' scores. What stores scored outranks how the base reads.
   */
  measuredModes: CopilotMeasuredMode[];
  /**
   * The base planogram's brands (its CDT 1 blocks), as laid out: how many
   * stand two or more shelves tall, and how many of those run into a
   * neighbouring bay as a strip under a quarter of it. Null when the base has
   * no layout to read.
   */
  baseBrandBlocks: { tall: number; strips: number } | null;
  /** The run whose store requests `fits` were measured on: its figures are that run's. */
  fitsRun?: { storePogTriggerId: number; createdAt: string };
  /** The last run these rules can be compared with, when there is one. */
  lastRun: { storePogTriggerId: number; createdAt: string } | null;
  /** The rules the last run used, so a turn can say what changed since. */
  lastRunRules?: PspRules | null;
  /**
   * The category's CDT levels as its tree names them (null where the tree gives
   * no name), and whether a level's groups are pack forms (sprays, sticks).
   * Absent where the tree was not read.
   */
  cdtLevels?: { level: number; name: string | null; packForm: boolean }[];
  /**
   * The last run's stores that generated, over those whose responses were read:
   * products placed and left out, and how many placed products could still
   * take another facing under their ceiling.
   * @deprecated No longer sent or read; kept so a fact sheet a page still holds stays valid.
   */
  lastRunHeadroom?: { placed: number; leftOut: number; belowCeiling: number };
  /** The rules as O set them up on the brief: what a turn compares with when there is no last run. */
  setupRules?: PspRules | null;
}

export type CopilotSettingGroup =
  "blocking" | "range" | "facings" | "strictness" | "fixtures" | "layout" | "engine" | "weight";

/**
 * - `live`: the value is sent and the solver reads it.
 * - `set_by_system`: generation sets this from the store's own data; the value here is a floor or is replaced.
 * - `no_effect`: nothing in generation reads it.
 * - `not_applicable`: live in general, idle under the rules as they stand.
 */
export type CopilotSettingStatus = "live" | "set_by_system" | "no_effect" | "not_applicable";

export interface CopilotSettingRow {
  /** Dot path into `PspRules`. */
  path: string;
  label: string;
  group: CopilotSettingGroup;
  help: string;
  value: unknown;
  /** The value of the proven rules for this blocking mode. */
  baseline: unknown;
  changed: boolean;
  status: CopilotSettingStatus;
  statusReason?: string;
  /** What generation sends, where that differs from `value`. */
  effective?: { value: unknown; reason: string };
}

export interface CopilotPatchOp {
  path: string;
  label: string;
  from: unknown;
  to: unknown;
  /** False: the key was absent before, not set to null. Absent on older ops: read `from` alone. */
  fromPresent?: boolean;
  /** False: the key is removed, not set to null. Absent on older ops: read `to` alone. */
  toPresent?: boolean;
  /** Parents of `path` that were absent before; Undo prunes them when left empty. */
  fromAbsentParents?: string[];
  /** Parents of `path` that were absent and are pruned when left empty. */
  toAbsentParents?: string[];
}

export interface CopilotPatch {
  id: string;
  title: string;
  /** Why, in one or two sentences. */
  why: string;
  /** What to expect on the planograms. */
  expect: string;
  /** What it may cost. */
  cost: string;
  evidence: CopilotEvidence;
  ops: CopilotPatchOp[];
}

export interface CopilotFinding {
  id: string;
  /** `blocker`: the rules cannot do what they say. `limit`: no setting changes this. */
  severity: "blocker" | "warning" | "limit" | "note";
  title: string;
  detail: string;
  paths: string[];
  evidence: CopilotEvidence;
  fix?: CopilotPatch;
  /** `run`: read off the last run's stores; `rules`: read off the rules on screen. */
  source: "rules" | "run";
  /** The stores it is about, at most six. */
  stores?: string[];
}

export interface CopilotStoreResult {
  /** Missing flags identify legacy results; false means the measurement is unavailable. */
  measured?: { shelves: boolean; products: boolean };
  store: string;
  storePogId: number;
  status: string;
  copiedFromBase?: boolean;
  error?: string;
  placed: number;
  unranged: number;
  aesthetics: number | null;
  bareShelves: number;
  /** Shelves under three quarters full. */
  lowShelves: number;
  brokenRules: { rule: string; count: number }[];
  /** The solve that ran out of time, when one did. */
  outOfTime: boolean;
  /** The share of the wall's shelf width filled, 0..1, where the response was read. */
  fill?: number;
  /** The penalties that cost most, largest first. */
  costliest: { term: string; amount: number }[];
  /** Of `unranged`, those taken out before the plan was made, and why in merch words. */
  leftOutBeforeSolve?: { count: number; reasons: string[] };
  /** Of `unranged`, those the run did not protect. Read off the store's request. */
  leftOutUnprotected?: number;
  /**
   * Of `unranged`, how many each cause applies to (a product can have more
   * than one): taken out before the plan, not protected, its block planned
   * narrower than its products at one facing, the store short of days of
   * supply, or a block or sequence break naming the product itself. Read off
   * the store's request and response.
   */
  leftOutWhy?: {
    beforeSolve: number;
    unprotected: number;
    widthShort: number;
    supply: number;
    broken: number;
  };
  /** Products the run protected, of the products it was sent. */
  protectedOf?: { protected: number; products: number };
  /** Placed products at their maximum facings. Read off the store's request. */
  atCeiling?: number;
  /**
   * The block the wall plan gave most width past what its products fill at
   * their maximum facings, in the request's lengths (hundredths), where one got
   * more than that: each shelf takes only the blocks planned for it, so the
   * rest of that width stays empty. Read off the store's request and response.
   */
  overWideBlock?: { planned: number; fills: number };
  /** The wall's shelf width, in the request's lengths (hundredths). */
  wall?: number;
  /**
   * Products whose units passed their maximum days of supply with more facings
   * than their minimum: what the units allowance moved. Read off the store's
   * request and response.
   */
  pastSupply?: number;
  /** Width products took past the data feed's facings cap (`max_facings`), in hundredths. */
  pastFeedCap?: number;
  /**
   * Width the wall plan gives its blocks past what their products fill at the
   * most fill writes (every facing its units allow, the feed's cap given way),
   * summed over the blocks, in hundredths: no price moves a shelf between
   * blocks, so that width stays empty whatever is set.
   */
  blockPlanEmpty?: number;
  /** What the store's request carried, where it was read. */
  request?: CopilotStoreRequestFacts;
}

/** What one store's request carried, as sent. */
export interface CopilotStoreRequestFacts {
  /** A gap cost at least what one block or sequence break did. */
  fillAtBlockPrice: boolean;
  /** Facings were held at their maximum (the ceiling was never). */
  facingsCapped: boolean;
  /** The wall was laid out from the base planogram's own plan. */
  baseMudmap: boolean;
  /** Products flagged core range. */
  coreRange: number;
  /** Products the run protected, core range included. */
  protectedCount: number;
}

export interface CopilotReadout {
  storePogTriggerId: number;
  finished: boolean;
  stores: CopilotStoreResult[];
  /**
   * Stores the run built from the data feed's labels (`cdt_source`
   * STORE_PRODUCT) on which the CDT Studio plan was refused because those
   * labels disagree with it, read off `output_store_pog_cdt_materialization`.
   * Absent where none.
   */
  studioPlanSkipped?: number;
  summary: {
    stores: number;
    done: number;
    failed: number;
    running: number;
    /** Stores at or above 80. */
    good: number;
    scored: number;
    unranged: number;
    bareShelves: number;
    lowShelves: number;
  };
}

/**
 * One decision that changes a planogram, as a switch. Each option not selected
 * carries the patch that selects it. `pick` marks O's recommendation.
 *
 * `brand_blocks` (vertical blocking only): "All stores" (`brand_blocks_all`),
 * "5+ bays" (`brand_blocks_5`), "None" (`brand_blocks_none`).
 */
export interface CopilotChoice {
  id: string;
  label: string;
  /** One short line of evidence, or empty. */
  note: string;
  /** `attention`: the selected option is not O's pick. */
  tone: "good" | "attention" | "neutral";
  options: { label: string; selected: boolean; pick: boolean; patch?: CopilotPatch }[];
}

/** Kept for older callers: a suggestion is a chip. */
export type CopilotSuggestion = CopilotChip;

/** The last store run of this base planogram, read off its store rows. */
export interface CopilotLastRun {
  storePogTriggerId: number;
  createdAt: string;
  stores: number;
  done: number;
  failed: number;
  running: number;
  /** Stores at or above 80. */
  good: number;
  scored: number;
  unranged: number;
  /** The lowest-scoring finished store. */
  lowest: { store: string; score: number } | null;
}

/** How the user picked something without typing: a chip or a switch option. Never re-parsed from text. */
export type CopilotPick =
  /** A move by its id. */
  | { kind: "move"; id: string }
  /** A switch option by its patch id (e.g. "use_vertical"). */
  | { kind: "option"; choice: string; option: string }
  /**
   * A finding's fix, by the finding's id. `patch`: the fix as the page holds it
   * (a Details finding the turn may not see); written through the same guards.
   */
  | { kind: "fix"; finding: string; patch?: CopilotPatch };

/** One click. Exactly one of `pick` (applied as picked) and `say` (sent as the user's words). */
export interface CopilotChip {
  label: string;
  pick?: CopilotPick;
  say?: string;
  /** Evidence-led explanation for a choice in the current question. */
  tradeoff?: {
    gain: string;
    cost: string;
    /** Set only when O recommends this option for the measured opening and current rules. */
    recommended?: boolean;
    /** Set only when the option matches the effective rule currently on screen. */
    selected?: boolean;
  };
}

/**
 * Who read the request. `grammar` needed no model; `model` is TypeSafe System One;
 * `offline`: nothing could read it.
 */
export type CopilotReader = "grammar" | "model" | "offline";

/** How the model read a turn: for logs and a debug tooltip. */
export interface CopilotReading {
  intent: string;
  confidence: number;
}

/**
 * One decision on screen that is not what the last run used, said as
 * `label: fromText → toText`: a switch in its own words ("Shelf heights:
 * Adjustable → Fixed"), anything else in the setting's ("Shelves: left part
 * empty → kept full").
 */
export interface CopilotRuleChange {
  /** Every path the row stands for; Undo writes each of them. */
  paths: string[];
  label: string;
  fromText: string;
  toText: string;
  /** The value before at each path, aligned with `paths`: what Undo writes. Null: not set, Undo removes it. */
  from: unknown[];
  /** The value on screen at each path, aligned with `paths`. */
  to: unknown[];
  /** Aligned with `paths`. False: the key was absent before, not set to null. */
  fromPresent?: boolean[];
  /** Aligned with `paths`. False: the key is absent on screen, not set to null. */
  toPresent?: boolean[];
  /** Aligned with `paths`: parents that were absent before and are pruned when left empty. */
  fromAbsentParents?: string[][];
  /** Aligned with `paths`: parents that were absent and are pruned when left empty. */
  toAbsentParents?: string[][];
}

/** A bay outline on a sketch, in base-planogram units. */
export interface CopilotSketchBay {
  xLeft: number;
  xRight: number;
}

/** A shelf row on a sketch. `bay`: index into the sketch's `bays`. */
export interface CopilotSketchShelf {
  bay: number;
  yBottom: number;
  yTop: number;
}

/** One brand's rectangle on one shelf row of one bay, in base-planogram units. */
export interface CopilotSketchBlock {
  /** "Other" for the brands drawn grey. */
  brand: string;
  /** Absent: the page picks it, stable per brand across sketches. */
  color?: string;
  xLeft: number;
  xRight: number;
  yBottom: number;
  yTop: number;
}

/** A mudmap of one option over the base's bays and shelves. Space without a block is empty. */
export interface CopilotSketch {
  /** The option drawn, e.g. "base", "rows", "columns", "clean", "brand_blocks_5". */
  option: string;
  /** e.g. "Columns". */
  label: string;
  /** e.g. "Columns: a sketch, not a promise". */
  caption: string;
  blocks: CopilotSketchBlock[];
  bays: CopilotSketchBay[];
  shelves: CopilotSketchShelf[];
}

/** The sketches beside an observation, shown side by side in this order. */
export interface CopilotSketchPair {
  /** What the choice costs downstream, for the enlarged view. */
  downstream: string;
  options: CopilotSketch[];
}

/** One "What I see" line, read off the base and the stores. */
export interface CopilotObservation {
  /** e.g. "wall", "brand_shape", "edges", "crowding", "supply", "fill", "stuck". */
  id: string;
  line: string;
  /** Only on a line that drives a switch. */
  sketch?: CopilotSketchPair;
}

export interface CopilotConciergeItem {
  id: string;
  title: string;
  detail: string;
  kind: "fact" | "proposal" | "decision" | "limitation";
  capability: "control" | "preference" | "output_acceptance" | "unavailable";
  provenance:
    | "base_measurement"
    | "store_measurement"
    | "current_rule"
    | "merchant_policy"
    | "capability"
    | "o_inference";
  status: "proposed" | "retained" | "accepted" | "needs_decision" | "known_limit";
  scope: "all" | "fresh" | "copy";
  settingPaths?: string[];
  observation?: CopilotObservation;
  choices?: CopilotChip[];
}

export interface CopilotConciergeSection {
  id: "plan" | "ambiguities" | "exceptions";
  title: string;
  items: CopilotConciergeItem[];
}

/** One "What I set" row: a switch as O set it, or as O left it. */
export interface CopilotSetupRow {
  /** e.g. "Blocking". */
  label: string;
  /** e.g. "Rows". */
  value: string;
  /** e.g. "Rows: CDT Studio isn't finalized." */
  reason: string;
  /** Present when O wrote it on this brief; the same entry as in `setupApplied`, for Undo. */
  change?: CopilotRuleChange;
}

/** A merchant's value O left alone though it would pick differently: "You chose {yours}; I'd pick {oWould}: {reason}". */
export interface CopilotKept {
  label: string;
  yours: string;
  oWould: string;
  reason: string;
}

/**
 * The last run, for the headline and the verdict line.
 * - `going`: stores open, within the time limit or runs ahead in the queue.
 * - `stuck`: every open store started over the time limit + 15 min ago.
 * - `stopped`: no store left open.
 * - `queued`: no store started yet.
 */
export type CopilotRunState = "going" | "stuck" | "stopped" | "queued";

export type CopilotBlock =
  | { type: "text"; text: string }
  | { type: "observation"; observation: CopilotObservation }
  | { type: "facts"; factSheet: CopilotFactSheet }
  | { type: "chips"; chips: CopilotChip[] }
  /** `raises`: what the checks would say if the patch were applied, where that is new. */
  | { type: "patch"; patch: CopilotPatch; raises?: CopilotFinding[] }
  | { type: "findings"; findings: CopilotFinding[] }
  | { type: "readout"; readout: CopilotReadout }
  | { type: "last_run"; lastRun: CopilotLastRun };

export interface CopilotMessage {
  role: "copilot" | "user";
  blocks: CopilotBlock[];
}

/** What the page holds when it asks: the rules on screen and what it measured. */
export interface CopilotContext {
  base_pog_id: number;
  psp_rules: PspRules;
  blocking?: CopilotBlockingReading | null;
}

export interface PayloadPostConfigCopilotBrief extends CopilotContext {
  /** Start from the rules the last run was generated with, when there is one. */
  start_from_last_run?: boolean;
}

export interface CopilotState {
  settings: CopilotSettingRow[];
  findings: CopilotFinding[];
  choices: CopilotChoice[];
  /** What holds on every store without being a choice, as short phrases. */
  alwaysOn: string[];
  /** Settings that differ from the proven rules. */
  changes: number;
  /** One-click asks under the thread: a pick by id, or words for the ask box. */
  suggestions: CopilotChip[];
}

/** Who read the request and what changed since the last run: on the brief and on every turn. */
export interface CopilotReaderState {
  reader: CopilotReader;
  /** Model turns only. */
  reading?: CopilotReading;
  /** The rules on screen against the last run's, or against O's setup (`setupRules`) when there is no run. */
  changesSinceLastRun: CopilotRuleChange[];
  reviewedSettings?: CopilotReviewedSetting[];
}

export interface CopilotReviewedSetting {
  path: string;
  value: unknown;
  kind: "set" | "confirmed";
  reason: string;
}

export interface ResponsePostConfigCopilotBrief extends CopilotState, CopilotReaderState {
  message: CopilotMessage;
  concierge?: CopilotConciergeSection[];
  conciergeReady?: boolean;
  factSheet: CopilotFactSheet;
  lastRun: CopilotLastRun | null;
  /** O's opening line: where things stand, in a sentence or two. */
  headline: string;
  /** The rules the brief was read on. Present when they are not the ones the page sent. */
  rules?: PspRules;
  /**
   * `o_picks`: no run to start from, so O applied its own picks to the rules on screen.
   * `o_setup`: O wrote its setup on every switch the merchant had not set (`setupApplied`).
   */
  rulesFrom: "screen" | "last_run" | "o_picks" | "o_setup";
  /** The rules the last run used. */
  lastRunRules: PspRules | null;
  /** "What I see": at most 6, in display order. */
  observations?: CopilotObservation[];
  /** Every change O wrote on this brief, each undoable. Empty: nothing written. */
  setupApplied?: CopilotRuleChange[];
  /** "What I set": one row per switch O shows, in display order. */
  setup?: CopilotSetupRow[];
  /** The merchant's values O kept but would pick differently; a value that blocks Generate comes first. */
  kept?: CopilotKept[];
  /** The one open ask's chips: at most 3. */
  asks?: CopilotChip[];
  /** Absent when there is no last run. */
  runState?: CopilotRunState;
}

/** One turn as the page sends it back. */
export interface CopilotHistoryTurn {
  role: "copilot" | "user";
  text: string;
  /**
   * On a reply that changed the rules: the rows the strip showed for that
   * change, each with its `paths`, `from` (before the reply) and `to` (after).
   * Undo and O's other reads of what a change wrote use these; the text is
   * read only where a page sends none.
   */
  changes?: CopilotRuleChange[];
}

export interface PayloadPostConfigCopilotTurn extends CopilotContext {
  /** What the user typed. One of `text` and `pick`. */
  text?: string;
  /** What the user clicked: executed by id, never parsed. */
  pick?: CopilotPick;
  /** The recent turns, oldest first: their words and, on a reply that changed the rules, its rows. */
  history?: CopilotHistoryTurn[];
  /** The review entries last shown to this client; the server verifies each against current rules. */
  reviewed_settings?: CopilotReviewedSetting[];
  fact_sheet?: CopilotFactSheet;
}

export interface ResponsePostConfigCopilotTurn extends CopilotState, CopilotReaderState {
  message: CopilotMessage;
  concierge?: CopilotConciergeSection[];
  conciergeReady?: boolean;
}

/**
 * 503 on a turn: the grammar read nothing and TypeSafe is not configured, failed
 * or took longer than 6 s. Chips, switches and the brief never return it.
 */
export interface ErrorPostConfigCopilotTurnUnavailable {
  code: "O_UNAVAILABLE";
  message: string;
}

export interface PayloadPostConfigCopilotReadout {
  base_pog_id: number;
  /** The run the headline described. Absent: the merchflow's latest store run. */
  store_pog_trigger_id?: number;
  psp_rules?: PspRules;
  blocking?: CopilotBlockingReading | null;
}

export interface ResponsePostConfigCopilotReadout {
  message: CopilotMessage;
  readout: CopilotReadout | null;
  findings: CopilotFinding[];
}

export interface ParamsGetConfigCopilotVerdict {
  /** The run the page shows. Absent: the merchflow's newest store run. */
  store_pog_trigger_id?: number;
}

/**
 * - `running`: stores are still being generated.
 * - `done`: the run is over and at least one store generated.
 * - `failed`: the run is over and no store generated.
 * - `none`: no store run for O to read.
 */
export type CopilotVerdictStatus = "running" | "done" | "failed" | "none";

/** O's verdict on a run where its stores land: the brief's headline, said of that run. */
export interface ResponseGetConfigCopilotVerdict {
  /** The base planogram the run was generated from, where the fix is made. */
  basePogId: number | null;
  /** One line; empty when `status` is `none`. */
  line: string;
  status: CopilotVerdictStatus;
  /** The run's first problem that a change of rules can fix. Absent while running. */
  fix?: {
    /** The finding id: what `o_fix` names on the base planogram page. */
    finding: string;
    /** The fix's own title, e.g. "Keep products below min DOS". */
    title: string;
    /** The link's words, e.g. "Fix and rerun S1533". */
    label: string;
  };
  lastRun: CopilotLastRun | null;
}
